import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserActionService, getUrlParams, join } from 'src/app/shared';
import { UserActions } from 'src/app/shared/models/user';
import { ClaimService, ClaimantService, InpageAliasService, TranslationService } from '..';
import { SentenceCasePipe } from '../../shared';
import { EncryptionService } from './encryption.service';
import { Session } from './session.service';
import { UserRoleService } from './user-role.service';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private _items = new BehaviorSubject<BreadcrumbItem[]>([]);
  items = this._items.asObservable();
  tempItems: any;
  claimant: any;
  mgrAction: UserActions | null;

  private rootPath: string;

  private rootLabel: string;

  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  constructor(
    private encrypt: EncryptionService,
    private userActions: UserActionService,
    private titleCasePipe: TitleCasePipe,
    private userRole: UserRoleService,
    private sentencecase: SentenceCasePipe,
    private session: Session,
    public claimService: ClaimService,
    private claimantService: ClaimantService,
    private translationService: TranslationService,
    private inpageAliasService: InpageAliasService
  ) {}

  public async build() {
    this.restoreRoot();
    const path = document.location.pathname;
    const title = document.title.replace(' - mySedgwick', '');
    const params = getUrlParams();
    // the default strategy for a page is to create a root element
    const pathLower = path?.toLowerCase();

    // Ensure breadcrumb translations are loaded (non-blocking for performance)
    this.translationRequest.contNum = Number(this.session.user.contNum);

    if (pathLower === '/act-as-delegate') {
      const actDelegateTranslationRequest = {
        ...this.translationRequest,
        group: 'Actdelegatepage',
        subGroup: 'Actdelegatepage',
      };
      // Load both page-specific and Shared translations for fallback
      Promise.all([
        this.translationService.getTranslation(actDelegateTranslationRequest),
        this.translationService.getTranslation(this.translationRequest),
      ]).then(() => {
        if (this._items.value.length > 0) {
          this.buildBreadcrumbsWithTranslations(path, title, params);
        }
      });
    } else if (pathLower === '/manage-delegate') {
      const manageDelegateTranslationRequest = {
        ...this.translationRequest,
        group: 'Managedelegatepage',
        subGroup: 'Managedelegatepage',
      };
      // Load both page-specific and Shared translations for fallback
      Promise.all([
        this.translationService.getTranslation(
          manageDelegateTranslationRequest
        ),
        this.translationService.getTranslation(this.translationRequest),
      ]).then(() => {
        if (this._items.value.length > 0) {
          this.buildBreadcrumbsWithTranslations(path, title, params);
        }
      });
    } else {
      this.translationService
        .getTranslation(this.translationRequest)
        .then(() => {
          if (this._items.value.length > 0) {
            this.buildBreadcrumbsWithTranslations(path, title, params);
          }
        });
    }

    try {
      switch (pathLower) {
        case '/claims':
        case '/return-to-work':
        case '/e-sign':
        case '/comm-center':
        case '/account-settings':
        case '/managedirectdeposit':
        case '/mileage-reimbursement':
        case '/inventory-submission':
        case '/pharmacy-card':
        case '/confirm-return-to-work':
        case '/help':
        case '/leavebalancesummary':
        case '/reportanabsence':
        case '/upload-documents-in-documentstab':
        case '/mileage-reimbursement-edit':
        case '/mileage-reimbursement-review':
        case '/my-work-schedule':
        case '/accommodations-pending':
        case '/manage-delegate':
        case '/act-as-delegate':
          await this.buildClaim(title, path);
          break;
        case pathLower.startsWith('/summary/') ? pathLower : '':
          this.buildSummary(title, path);
          break;
        case '/preferencespage':
          if (params.claimId) {
            this.buildClaim(title, path);
          } else {
            this.buildRoot(title, path);
          }
          break;
        case '/dashboard':
          {
            if (
              this.userRole.employee ||
              (this.userRole.manager && !params.emp)
            ) {
              this.buildRoot(title, path);
            } else if (this.userRole.manager && params.emp) {
              this.buildEmployee();
            }
          }
          break;
        default:
          this.buildRoot(title, path);
          break;
      }
    } catch (error) {
      this.setGenericError(error);
    }
  }

  private removeDuplicates(items: BreadcrumbItem[]): BreadcrumbItem[] {
    const uniqueItems: BreadcrumbItem[] = [];

    items.forEach((item) => {
      if (
        !uniqueItems.find(
          (uniqueItem) =>
            uniqueItem.label?.toLowerCase() === item.label?.toLowerCase() &&
            uniqueItem.path === item.path
        )
      ) {
        uniqueItems.push(item);
      }
    });

    return uniqueItems;
  }

  private setItems(items: BreadcrumbItem[]) {
    const uniqueItems = this.removeDuplicates(items);
    this._items.next(uniqueItems);

    // save the breadcrumb to session storge so we can restore it after a page refresh
    sessionStorage.setItem(
      'breadcrumb' + this.session.user.sessionId,
      JSON.stringify(uniqueItems)
    );
  }

  private restoreRoot(): void {
    // root is null, this was in initial load or page refresh
    if (!this.rootPath && !this.rootLabel) {
      // if we have a breadcrumn in storage, apply it
      const breadcrumbData = sessionStorage.getItem(
        'breadcrumb' + this.session.user.sessionId
      );
      if (breadcrumbData) {
        const items: BreadcrumbItem[] = JSON.parse(breadcrumbData);
        this.rootPath = items[0].path;
        this.rootLabel = items[0].key ?? items[0].label;
      }
    }
  }

  private buildRoot(label: string, path: string): void {
    this.rootLabel = label;
    this.rootPath = path;

    this.setItems([
      {
        key: label,
        label: this.translationService.getTranslationForKey(
          label,
          this.translationRequest
        ),
        path,
        ariaLabel: this.translationService.getTranslationForKey(
          this.rootLabel,
          this.translationRequest
        ),
      },
    ]);
  }

  private buildSummary(label: string, path: string): void {
    const translatedLabel = this.translationService.getTranslationForKey(
      label,
      this.translationRequest
    );

    this.setItems([
      {
        key: 'Summary view',
        label: this.translationService.getTranslationForKey(
          'Summary view',
          this.translationRequest
        ),
        path: '/summary',
        ariaLabel: this.translationService.getTranslationForKey(
          'Redirects to Summary view',
          this.translationRequest
        ),
      },
      {
        key: label,
        label: translatedLabel,
        path: path,
        ariaLabel: this.translationService.getTranslationForKey(
          `Redirects to ${label}`,
          this.translationRequest
        ),
      },
    ]);
  }

  private async buildClaim(title: string, path: string) {
    const params = getUrlParams();
    const commCenterPath = '/communication-center';
    const claimId = params.claimId;
    const source = params.source;
    this.mgrAction = this.userActions.getAction();
    const items: BreadcrumbItem[] = [];
    const employeeAlias = await this.inpageAliasService.get('employeeSingular');
    if (
      (this.userRole.manager &&
        ((this.mgrAction !== UserActions.ViewOwnClaim &&
          this.mgrAction !== UserActions.ViewOwnEmpDb) ||
          params.emp)) ||
      this.userRole.controller ||
      path === commCenterPath
    ) {
      items.push({
        key: this.rootLabel,
        label: this.translationService.getTranslationForKey(
          this.rootLabel,
          this.translationRequest
        ),
        path: this.rootPath,
        ariaLabel:
          this.translationService.getTranslationForKey(
            'Redirect to',
            this.translationRequest
          ) + ' ' +
          this.translationService.getTranslationForKey(
            this.rootLabel,
            this.translationRequest
          ),
      });
    }

    if ((claimId && source) || path === commCenterPath) {
      this.claimant = await this.getClaimant();
      // if root is my-claims, then do not show employee segment
      if (
        this.claimant &&
        this.rootPath !== '/my-claims' &&
        !this.claimant.userOwns &&
        !this.userRole.controller
      ) {
        const empID = this.claimant?.empId
          ? this.claimant.empId
          : this.claimant.employeeID
          ? this.claimant.employeeID
          : this.claimant.employeeId; //as multiple objects are being used as claimant need to check each property

        items.push({
          label: this.titleCasePipe.transform(
            join([this.claimant.name, empID])
          ),
          path: '/dashboard',
          queryParams: {
            emp: this.encrypt.encrypt(empID),
          },
          ariaLabel:
            this.translationService.getTranslationForKey(
              'Redirect to',
              this.translationRequest
            ) + ' ' + employeeAlias + ' ' +
            this.translationService.getTranslationForKey(
              'Dashboard for',
              this.translationRequest
            ) + ' ' + this.claimant.name,
        });
      }
      if (
        (this.claimant &&
          path !== '/my-claims' &&
          this.claimant.userOwns &&
          !this.userRole.controller) ||
        (path === commCenterPath && this.rootPath === commCenterPath)
      ) {
        items.push({
          key: 'My claims',
          label: this.translationService.getTranslationForKey(
            'My claims',
            this.translationRequest
          ),
          path: '/dashboard',
          ariaLabel:
            this.translationService.getTranslationForKey(
              'Redirect to',
              this.translationRequest
            ) + ' ' + employeeAlias + ' ' +
            this.translationService.getTranslationForKey(
              'dashboard',
              this.translationRequest
            ),
        });
      }

      if (
        this.claimant &&
        this.claimant.claimDescription &&
        this.claimant.claimNumber &&
        claimId &&
        source
      ) {
        items.push({
          label: join([
            this.translationService.getTranslationForKey(
              this.titleCasePipe.transform(this.claimant?.claimDescription),
              this.translationRequest
            ),
            this.claimant?.claimNumber,
          ]),
          path: '/claims',
          queryParams: {
            claimId,
            source,
          },
          ariaLabel:
            this.translationService.getTranslationForKey(
              'Redirect to claim',
              this.translationRequest
            ) + ' ' + this.claimant?.claimNumber,
        });
      }

      // add another segment for claim child pages such as return-to-work
      if (path !== '/claims' && path !== '/upload-documents-in-documentstab') {
        const childPageKey = path === '/e-sign' ? params.name : title;
        items.push({
          key: childPageKey,
          label: this.translationService.getTranslationForKey(
            childPageKey,
            this.translationRequest
          ),
          path,
          queryParams: params,
          ariaLabel: this.translationService.getTranslationForKey(
            'Redirect to Esign page',
            this.translationRequest
          ),
        });
      }

      this.setItems(items);
    } else {
      // These routes can be a root page or accessed from
      // a claim
      if (
        path === '/account-settings' ||
        path === '/help' ||
        path === '/my-work-schedule' ||
        path === '/manage-delegate' ||
        path === '/act-as-delegate'
      ) {
        this.buildRoot(title, path);
      }
      if (path === '/communication-center') {
        items.push({
          key: title,
          label: this.translationService.getTranslationForKey(
            title,
            this.translationRequest
          ),
          path,
          queryParams: params,
          ariaLabel: this.translationService.getTranslationForKey(
            'Redirect to Communication Center',
            this.translationRequest
          ),
        });

        this.setItems(items);
      }
    }
  }

  private async buildEmployee() {
    this.claimant = await this.getClaimant();
    const params = getUrlParams();
    this.mgrAction = this.userActions.getAction();
    this.tempItems = [];

    this.tempItems.push({
      key: this.rootLabel,
      label: this.translationService.getTranslationForKey(
        this.rootLabel,
        this.translationRequest
      ),
      path: this.rootPath,
    });

    const employeeId = await this.claimantService.getEmpId();
    if (employeeId && this.claimant && this.claimant.name) {
      this.tempItems.push({
        label: this.titleCasePipe.transform(
          join([this.claimant.name, employeeId])
        ),
        path: '/dashboard',
        queryParams: { emp: params.emp },
        ariaLabel: this.translationService.getTranslationForKey(
          'Redirect to employee dashboard',
          this.translationRequest
        ),
      });
    }

    this.setItems(this.tempItems);
  }

  private buildSupervsior(title: string, path: string): void {
    this.buildRoot(title, path);
  }

  private setGenericError(error: any) {
    // eslint-disable-next-line no-console
    console.error(error);
    this.setItems([
      {
        label: 'Breadcrumb Error',
        path: null,
        ariaLabel: 'error',
      },
    ]);
  }

  private async getClaimant() {
    let claimant = null;
    const params = getUrlParams();
    const claimId = params.claimId;
    const source = params.source;
    const lob = params.lob;
    if (source && claimId) {
      claimant = await this.claimService.getSummary(
        source,
        claimId,
        false,
        false,
        lob
      );
    } else {
      claimant = this.userActions.getClaimant();
    }
    return claimant;
  }

  private buildBreadcrumbsWithTranslations(
    path: string,
    title: string,
    params: any
  ) {
    const pathLower = path?.toLowerCase();

    try {
      switch (pathLower) {
        case '/manage-delegate':
        case '/act-as-delegate':
          // Try page-specific translation first, fallback to Shared group if not found
          let pageSpecificTranslationRequest = this.translationRequest;
          if (pathLower === '/act-as-delegate') {
            pageSpecificTranslationRequest = {
              ...this.translationRequest,
              group: 'Actdelegatepage',
              subGroup: 'Actdelegatepage',
            };
          } else if (pathLower === '/manage-delegate') {
            pageSpecificTranslationRequest = {
              ...this.translationRequest,
              group: 'Managedelegatepage',
              subGroup: 'Managedelegatepage',
            };
          }

          let translatedLabel = this.translationService.getTranslationForKey(
            title,
            pageSpecificTranslationRequest
          );

          // If translation not found (returns original key), try Shared group as fallback
          if (translatedLabel === title) {
            translatedLabel = this.translationService.getTranslationForKey(
              title,
              this.translationRequest
            );
          }

          this.setItems([
            {
              key: title,
              label: translatedLabel,
              path,
              ariaLabel: title,
            },
          ]);
          break;
        case '/account-settings':
        case '/help':
        case '/my-work-schedule':
          this.buildRoot(title, path);
          break;
        default:
          const currentItems = this._items.value;
          if (currentItems.length > 0) {
            const updatedItems = currentItems.map((item) => {
              const translatedLabel = item.key
                ? this.translationService.getTranslationForKey(
                    item.key,
                    this.translationRequest
                  )
                : item.label;
              return {
                ...item,
                label: translatedLabel,
              };
            });
            this.setItems(updatedItems);
          }
          break;
      }
    } catch (error) {}
  }
}

export class BreadcrumbItem {
  key?: string;
  label: string;
  path: string;
  queryParams?: any;
  ariaLabel: string;
}
